// src/screens/RetirementStandard.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 12 Section 5.1 + Brief 46A Section 6 — RLSA Retirement Standard Picker
// Three cards with expandable detail, % of income labels, "build your own"
// toggle, PLSA source link.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { PLSA } from '../config/plsaConfig';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

// ── What's included content per standard ─────────────────────────────────────
const INCLUDED: Record<string, string[]> = {
  minimum: [
    'Housing: basic DIY maintenance only',
    'Food: ~£55/week groceries, limited dining out',
    'Transport: no car, bus pass, occasional taxi',
    'Holidays: one week UK holiday per year',
  ],
  moderate: [
    'Housing: £500/year maintenance + contingency',
    'Food: ~£56/week groceries, weekly dining out',
    'Transport: small car replaced every 7 years',
    'Holidays: one fortnight abroad + UK weekend break',
  ],
  comfortable: [
    'Housing: £600/year maintenance + contingency',
    'Food: ~£75/week groceries, frequent dining out',
    'Transport: small car replaced every 5 years',
    'Holidays: fortnight 4-star abroad + 3 UK weekend breaks',
  ],
};

export default function RetirementStandard({ data, onUpdate }: Props) {
  const isCouple = data.isCouple;
  const selected = data.plsaStandard;
  const rlsaMode = data.rlsaMode || 'plsa';

  // Gross income for % calculations
  const grossIncome = (data.salary || 0) + (data.dividends || 0) + (data.rentalIncomeNet ?? 0);

  // Expandable detail state per card
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const standards: Array<{
    key: 'minimum' | 'moderate' | 'comfortable';
    label: string;
    annual: number;
    desc: string;
    color: string;
    borderColor: string;
  }> = [
    {
      key: 'minimum',
      label: 'Minimum',
      annual: isCouple ? PLSA.couple.minimum : PLSA.single.minimum,
      desc: 'Covers basic needs only. No car, no holiday. Very limited dining out.',
      color: 'bg-gray-50',
      borderColor: 'border-gray-300',
    },
    {
      key: 'moderate',
      label: 'Moderate',
      annual: isCouple ? PLSA.couple.moderate : PLSA.single.moderate,
      desc: 'Some flexibility. A holiday a year in Europe. Occasional dining out. A used car.',
      color: 'bg-blue-50',
      borderColor: 'border-blue-300',
    },
    {
      key: 'comfortable',
      label: 'Comfortable',
      annual: isCouple ? PLSA.couple.comfortable : PLSA.single.comfortable,
      desc: 'Regular holidays. Car replacement every 5 years. Dining out weekly. Theatre, gym.',
      color: 'bg-amber-50',
      borderColor: 'border-amber-300',
    },
  ];

  const handleSelect = (key: 'minimum' | 'moderate' | 'comfortable') => {
    const annual = isCouple ? PLSA.couple[key] : PLSA.single[key];
    onUpdate({
      plsaStandard: key,
      rlsaAnnualTarget: annual,
      annualRetirementIncomeNeeded: annual,
    });
  };

  const currentTarget = selected
    ? (isCouple ? PLSA.couple[selected] : PLSA.single[selected])
    : 0;

  // Phase % defaults derived from current PLSA selection
  const defaultPhase1Pct = grossIncome > 0 && selected
    ? Math.round((isCouple ? PLSA.couple.comfortable : PLSA.single.comfortable) / grossIncome * 100)
    : 0;
  const defaultPhase2Pct = grossIncome > 0 && selected
    ? Math.round((isCouple ? PLSA.couple.moderate : PLSA.single.moderate) / grossIncome * 100)
    : 0;
  const defaultPhase3Pct = grossIncome > 0 && selected
    ? Math.round((isCouple ? PLSA.couple.minimum : PLSA.single.minimum) / grossIncome * 100)
    : 0;

  const phase1Pct = data.phase1IncomePct || defaultPhase1Pct;
  const phase2Pct = data.phase2IncomePct || defaultPhase2Pct;
  const phase3Pct = data.phase3IncomePct || defaultPhase3Pct;

  const handleModeChange = (mode: 'plsa' | 'percentage') => {
    if (mode === 'percentage') {
      onUpdate({
        rlsaMode: 'percentage',
        phase1IncomePct: defaultPhase1Pct,
        phase2IncomePct: defaultPhase2Pct,
        phase3IncomePct: defaultPhase3Pct,
        phase1Income: Math.round(defaultPhase1Pct / 100 * grossIncome),
        phase2Income: Math.round(defaultPhase2Pct / 100 * grossIncome),
        phase3Income: Math.round(defaultPhase3Pct / 100 * grossIncome),
      });
    } else {
      onUpdate({ rlsaMode: 'plsa' });
    }
  };

  const handlePhasePctChange = (phase: 1 | 2 | 3, pct: number) => {
    const income = Math.round(pct / 100 * grossIncome);
    if (phase === 1) onUpdate({ phase1IncomePct: pct, phase1Income: income });
    if (phase === 2) onUpdate({ phase2IncomePct: pct, phase2Income: income });
    if (phase === 3) onUpdate({ phase3IncomePct: pct, phase3Income: income });
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-ma-navy mb-1">Retirement Standard</h2>
      <p className="text-gray-500 text-sm mb-6">
        What standard of living do you want in retirement? Based on the 2024/25 Pensions and Lifetime Savings Association (PLSA) Retirement Living Standards.
      </p>

      {/* Single / Couple toggle */}
      <div className="flex items-center gap-3 mb-8">
        <span className="text-sm text-gray-600">Showing figures for:</span>
        <button
          onClick={() => onUpdate({ isCouple: false })}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            !isCouple ? 'bg-ma-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Single
        </button>
        <button
          onClick={() => onUpdate({ isCouple: true })}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            isCouple ? 'bg-ma-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Couple
        </button>
      </div>

      {/* Mode toggle */}
      <div className="flex items-center gap-3 mb-6">
        <label className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          rlsaMode === 'plsa' ? 'bg-ma-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}>
          <input
            type="radio" name="rlsaMode" checked={rlsaMode === 'plsa'}
            onChange={() => handleModeChange('plsa')}
            className="sr-only"
          />
          Use PLSA standard
        </label>
        <label className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          rlsaMode === 'percentage' ? 'bg-ma-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}>
          <input
            type="radio" name="rlsaMode" checked={rlsaMode === 'percentage'}
            onChange={() => handleModeChange('percentage')}
            className="sr-only"
          />
          Set by % of income
        </label>
      </div>

      {/* Three PLSA cards */}
      {rlsaMode === 'plsa' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-4">
            {standards.map(s => (
              <div key={s.key}>
                <button
                  onClick={() => handleSelect(s.key)}
                  className={`w-full rounded-2xl border-2 p-6 text-left transition-all ${
                    selected === s.key
                      ? 'border-ma-navy bg-ma-navy/5 ring-2 ring-ma-navy/20 shadow-lg'
                      : `${s.borderColor} ${s.color} hover:shadow-md`
                  }`}
                >
                  <div className={`text-lg font-bold mb-2 ${selected === s.key ? 'text-ma-navy' : 'text-gray-800'}`}>
                    {s.label}
                  </div>
                  <div className="text-3xl font-bold text-[#C9A84C] mb-1">
                    {fmt(s.annual)}
                    <span className="text-sm text-gray-400 font-normal">/year</span>
                  </div>
                  {/* Change 1 — % of current gross */}
                  {grossIncome > 0 && (
                    <div className="text-xs text-gray-400 mb-3">
                      {Math.round(s.annual / grossIncome * 100)}% of your current income
                    </div>
                  )}
                  <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                  {selected === s.key && (
                    <div className="mt-3 text-xs font-medium text-ma-navy">✓ Selected</div>
                  )}

                  {/* Change 4 — Expandable detail */}
                  <div
                    className="mt-3 text-xs text-gray-400 cursor-pointer hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(prev => ({ ...prev, [s.key]: !prev[s.key] }));
                    }}
                  >
                    {expanded[s.key] ? '▾ What\'s included' : '▸ What\'s included'}
                  </div>
                </button>
                {expanded[s.key] && (
                  <div className="mt-1 bg-white border border-gray-200 rounded-xl p-4 space-y-1">
                    {INCLUDED[s.key].map((item, i) => (
                      <div key={i} className="text-xs text-gray-600">• {item}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Change 3 — PLSA source link */}
          <div className="mb-8">
            <a
              href="https://www.retirementlivingstandards.org.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-ma-navy transition-colors"
            >
              View full breakdown →
            </a>
          </div>
        </>
      )}

      {/* Change 2 — % of income alternative input */}
      {rlsaMode === 'percentage' && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
          <h3 className="text-sm font-bold text-ma-navy mb-4">Build your own target</h3>
          {grossIncome <= 0 && (
            <p className="text-sm text-gray-500">Enter income details first to use percentage-based targets.</p>
          )}
          {grossIncome > 0 && (
            <div className="space-y-4">
              {[
                { phase: 1 as const, label: 'Phase 1 (comfortable)', ageRange: `age ${data.targetFreedomAge || 57} to 75`, pct: phase1Pct },
                { phase: 2 as const, label: 'Phase 2 (moderate)', ageRange: 'age 75 to 85', pct: phase2Pct },
                { phase: 3 as const, label: 'Phase 3 (minimum)', ageRange: 'age 85+', pct: phase3Pct },
              ].map(p => (
                <div key={p.phase} className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-700">{p.label}</div>
                    <div className="text-xs text-gray-400">{p.ageRange}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={p.pct}
                      onChange={e => handlePhasePctChange(p.phase, Number(e.target.value) || 0)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
                    />
                    <span className="text-sm text-gray-500">%</span>
                    <span className="text-sm text-gray-400 ml-2">→</span>
                    <span className="text-sm font-medium text-ma-navy ml-2">
                      {fmt(Math.round(p.pct / 100 * grossIncome))}/yr
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Target display */}
      {rlsaMode === 'plsa' && currentTarget > 0 && (
        <div className="bg-ma-navy rounded-2xl p-8 text-center text-white">
          <div className="text-white/60 text-sm mb-2">Your target retirement income</div>
          <div className="text-5xl font-bold text-[#C9A84C]">{fmt(currentTarget)}/year</div>
        </div>
      )}

      {rlsaMode === 'percentage' && grossIncome > 0 && (
        <div className="bg-ma-navy rounded-2xl p-8 text-center text-white">
          <div className="text-white/60 text-sm mb-2">Your target retirement income (Phase 1)</div>
          <div className="text-5xl font-bold text-[#C9A84C]">{fmt(Math.round(phase1Pct / 100 * grossIncome))}/year</div>
          <div className="text-white/40 text-xs mt-2">
            Stepping down to {fmt(Math.round(phase2Pct / 100 * grossIncome))}/yr at 75, then {fmt(Math.round(phase3Pct / 100 * grossIncome))}/yr at 85+
          </div>
        </div>
      )}
    </div>
  );
}
